import consola from 'consola'
import { useSessionManager } from '~/entities/auth/model'
import { useTokenRefreshV2, useLogoutHandler } from '~/features/auth/model'

/**
 * Плагин для автоматического мониторинга и обновления токенов
 * 
 * ✅ НОВАЯ ЛОГИКА (без привязки к sessionId):
 * - Проверяет счетчик неудачных попыток refresh
 * - НЕ пытается refresh если уже 2+ неудачных попытки подряд
 * - Работает только при фокусе окна (не постоянный setInterval)
 * - Полагается на 401 interceptor в API для основной логики
 */
export default defineNuxtPlugin(() => {
  // Только на клиенте
  if (process.client) {
    const sessionManager = useSessionManager()
    const tokenRefreshV2 = useTokenRefreshV2()
    const logoutHandler = useLogoutHandler()

    // Флаг для предотвращения множественных обновлений
    let lastRefreshTime = 0
    const REFRESH_COOLDOWN = 10_000 // 10 секунд между попытками

    // Функция для обновления токена
    const refreshTokenIfNeeded = async () => {
      // ❌ НЕ пытаемся если уже были неудачные попытки (токен invalid)
      if (logoutHandler.isRefreshTokenInvalid()) {
        consola.log('[TokenMonitor] ❌ Refresh token помечен как недействительный, пропускаем')
        return false
      }
      
      // ❌ НЕ пытаемся если много неудачных попыток подряд
      const failureCount = logoutHandler.getRefreshTokenFailureCount()
      if (failureCount >= 2) {
        consola.log(`[TokenMonitor] ❌ Слишком много неудач (${failureCount}), пропускаем`)
        return false
      }
      
      // Проверяем время последнего обновления (увеличен cooldown)
      const now = Date.now()
      if (now - lastRefreshTime < REFRESH_COOLDOWN) {
        consola.log('[TokenMonitor] ⏰ Cooldown активен, пропускаем обновление')
        return false
      }
      
      lastRefreshTime = now
      
      // ✅ Если нет access token - пытаемся обновить
      if (!sessionManager.authAccessToken.value) {
        consola.log('[TokenMonitor] 🔄 Access token отсутствует, пытаемся восстановить через refresh token')
        const startTime = Date.now()
        const newToken = await tokenRefreshV2.refreshToken()
        const duration = Date.now() - startTime
        
        if (newToken) {
          consola.log(`[TokenMonitor] ✅ Токен успешно обновлен за ${duration}ms`)
        } else {
          consola.log(`[TokenMonitor] ⚠️ Не удалось обновить токен (${duration}ms)`)
        }
        
        return !!newToken
      }
      
      return false
    }

    // ✅ УБРАН setInterval - не проверяем постоянно!
    // Основная логика refresh теперь в 401 interceptor (API.ts)
    
    // ✅ Проверяем токены ТОЛЬКО при фокусе окна (пользователь вернулся)
    window.addEventListener('focus', () => {
      if (!sessionManager.authAccessToken.value && sessionManager.hasActiveSession.value) {
        consola.log('[TokenMonitor] 🖥️ Окно получило фокус, проверяем токены')
        refreshTokenIfNeeded()
      }
    })

    // ✅ Проверяем токены ТОЛЬКО при видимости страницы (пользователь переключился)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && !sessionManager.authAccessToken.value && sessionManager.hasActiveSession.value) {
        consola.log('[TokenMonitor] 👁️ Страница стала видимой, проверяем токены')
        refreshTokenIfNeeded()
      }
    })

    consola.log('[TokenMonitor] 🟢 Плагин мониторинга токенов V2 запущен (проверка при focus/visibility)')
  }
}) 